import React from 'react'
import { IHolviAccount } from '@query/holvi.ts'
import { Alert, Badge, Button, TextTooltip } from '@components'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { BankAccount } from '@pages/SettingsPage/sections/IntegrationSettings/BankAccount.tsx'
import { Link } from 'react-router-dom'
import { getBankIntegrationPageURL } from '@constants'
import { useBusinessContext, useMobileEmbed } from '@context'
import { UserPicker } from '@containers/UserPicker'
import { FaShieldAlt } from 'react-icons/fa'
import { FaUserCheck } from 'react-icons/fa6'
import { getMobileAwarePath } from '@root/utils/getMobileAwarePath'
import { HolviAuthorizeButton } from '@containers/HolviAuthorizeButton/HolviAuthorizeButton.tsx'

interface Props {
  accounts: IHolviAccount[]
  showReconnect?: boolean
  reconnectRedirectUrl?: string
}

export const HolviAccountMapper: React.FC<Props> = ({
  accounts,
  showReconnect = false,
  reconnectRedirectUrl
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const { isEmbedded } = useMobileEmbed()

  const accountHeader = {
    psd: t('settings.holvi.account.psd'),
    credit: t('settings.holvi.account.credit')
  }

  return (
    <AccountsWrapper>
      {showReconnect && reconnectRedirectUrl && (
        <Alert
          type="warning"
          title={t('settings.holvi.reconnectTitle')}
          description={
            <AlertContent>
              <p>{t('settings.holvi.reconnectMessage')}</p>
              <HolviAuthorizeButton
                redirectUrl={reconnectRedirectUrl}
                label={t('settings.holvi.reconnectButton')}
                intent="warning"
                size="sm"
                style={{ alignSelf: 'flex-start' }}
              />
            </AlertContent>
          }
        />
      )}

      {accounts
        // Show only enabled accounts
        .filter(({ enabled }) => enabled)
        .map(({ id, iban, last_sync_at, type, name, account_owner, account_admins }, idx) => (
          <BankAccount
            key={`account-${idx}`}
            accountId={id}
            accountName={name || accountHeader[type]}
            accountIdentifier={iban}
            lastSyncAt={last_sync_at}
            isMissingInfo={false}
            renderFooter={() =>
              account_owner && (
                <UserPicker
                  value={[account_owner, ...(account_admins || [])]}
                  disabled
                  renderItem={({ user_id, email }) => {
                    if (user_id === account_owner) {
                      return (
                        <TextTooltip tooltip={t('holviConnection.ownerSettings.tooltip.owner')}>
                          <Badge key={user_id}>
                            <FaShieldAlt />
                            <span>{email}</span>
                          </Badge>
                        </TextTooltip>
                      )
                    }
                    return (
                      <TextTooltip tooltip={t('holviConnection.ownerSettings.tooltip.admin')}>
                        <Badge key={user_id}>
                          <FaUserCheck />
                          <span>{email}</span>
                        </Badge>
                      </TextTooltip>
                    )
                  }}
                />
              )
            }
          />
        ))}

      {!showReconnect && (
        <ActionsWrapper>
          <Link to={getMobileAwarePath(getBankIntegrationPageURL(slug, 'holvi-edit'), isEmbedded)}>
            <Button>{t('general.edit')}</Button>
          </Link>
        </ActionsWrapper>
      )}
    </AccountsWrapper>
  )
}

const AccountsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-wrap: wrap;
`

const AlertContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
